import React from 'react';
import AutoScroll from 'embla-carousel-auto-scroll';
import Link from 'next/link';
import Image, { StaticImageData } from 'next/image';
import { cn } from '@/utils/styles';
import tokyoMap from '@/public/images/landing/hero/tokyo-map.png';
import singaporeMap from '@/public/images/landing/hero/singapore-map.png';
import laMap from '@/public/images/landing/hero/la-map.png';
import parisMap from '@/public/images/landing/hero/paris-map.png';
import nyMap from '@/public/images/landing/hero/ny-map.png';
import austinMap from '@/public/images/landing/hero/austin-map.png';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '../shadcn/carousel/carousel';
import MediaContainer from '../ui/shared/MediaContainer';
import { H6 } from '../shadcn/typography';
import { Button } from '../shadcn/button';

const commonImageClasses = 'object-cover size-full';

export interface MapItem {
  href: string;
  imageSrc: StaticImageData;
  name: string;
}

export interface MapCarouselItemProps {
  item: MapItem;
  forceOverlay?: boolean;
  className?: string;
}

export const mapItems: MapItem[] = [
  {
    href: '/network/regions/Tokyo/metrics',
    imageSrc: tokyoMap,
    name: 'Tokyo, Japan',
  },
  {
    href: '/network/regions/Singapore/metrics',
    imageSrc: singaporeMap,
    name: 'Singapore',
  },
  {
    href: '/network/regions/Los%20Angeles-Long%20Beach,%20CA/metrics',
    imageSrc: laMap,
    name: 'Los Angeles, California',
  },
  {
    href: '/network/regions/ile-de-france/metrics',
    imageSrc: parisMap,
    name: 'Paris, France',
  },
  {
    href: '/network/regions/New%20York-Newark,%20NY-NJ-CT-PA/metrics',
    imageSrc: nyMap,
    name: 'New York, New York',
  },
  {
    href: '/network/regions/Austin-Round%20Rock-Georgetown,%20TX/metrics',
    imageSrc: austinMap,
    name: 'Austin, Texas',
  },
];

export const MapCarouselItem = ({
  item,
  className,
  forceOverlay,
}: MapCarouselItemProps) => {
  return (
    <MediaContainer
      className={cn(
        'relative size-full cursor-pointer !rounded-md !border-0 transition-colors duration-500 hover:!border hover:border-neutral-700 lg:!rounded-2xl [&_.content-overlay]:hover:opacity-100',
        className,
      )}>
      <div
        className={cn(
          'content-overlay absolute left-0 top-0 flex size-full items-center justify-center bg-neutral-1000/70 transition-opacity',
          {
            'opacity-0': !forceOverlay,
            'opacity-100': forceOverlay,
          },
        )}>
        <div className="flex flex-col items-center gap-6">
          <H6 className="font-semibold leading-none">{item.name}</H6>
          <Link href={item.href}>
            <Button className="rounded-full text-sm tracking-wider">
              Explore →
            </Button>
          </Link>
        </div>
      </div>
      <Image
        src={item.imageSrc}
        alt={item.name}
        className={cn(commonImageClasses)}
      />
    </MediaContainer>
  );
};

const MapCarousel = () => {
  return (
    <Carousel
      className="z-10"
      showMasks
      opts={{
        loop: true,
        dragFree: true,
      }}
      plugins={[
        AutoScroll({
          speed: 0.7,
          stopOnInteraction: false,
          stopOnFocusIn: false,
          stopOnMouseEnter: false,
          startDelay: 0,
        }),
      ]}>
      <CarouselContent className="z-10 -ml-5 w-screen lg:-ml-10 lg:w-screen">
        {[...mapItems, ...mapItems, ...mapItems].map((item, index) => (
          <CarouselItem
            className="box-content h-[180px] basis-[calc(100%-100px)] !pl-5 md:basis-[180px] lg:h-[204px] lg:basis-[360px] lg:!pl-5"
            key={item.name + index}>
            <MapCarouselItem item={item} />
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  );
};

export default MapCarousel;
